import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiArrowLeft, FiChevronDown, FiChevronUp, FiCheck } from 'react-icons/fi';
import { services } from '../utils/servicesData';

export default function ServiceDetail() {
  const { id } = useParams();
  const service = services.find(s => s.id === id);
  const [openFaq, setOpenFaq] = useState(null);

  if (!service) return <Navigate to="/services" replace />;

  const others = services.filter(s => s.id !== id).slice(0, 3);

  return (
    <div style={{ paddingTop: 74 }}>
      {/* Hero */}
      <section style={{ position: 'relative', height: 420, overflow: 'hidden' }}>
        <img src={service.heroImg} alt={service.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(105deg, rgba(6,15,30,0.9) 0%, rgba(11,37,69,0.8) 45%, rgba(11,37,69,0.3) 100%)' }} />
        <div className="container" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', paddingBottom: '3.5rem' }}>
          <div style={{ maxWidth: 620 }}>
            <Link to="/services" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem', marginBottom: '1.25rem' }}>
              <FiArrowLeft style={{ fontSize: '0.85rem' }} /> All Services
            </Link>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '1.75rem' }}>{service.icon}</span>
              <span className="label">{service.tagline}</span>
            </div>
            <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'white', lineHeight: 1.12 }}>{service.title}</h1>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-pad">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '4rem', alignItems: 'start' }}>
            {/* Left */}
            <div>
              {/* Summary */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <p style={{ fontSize: '1.1rem', color: 'var(--navy)', fontWeight: 800, lineHeight: 1.75, marginBottom: '2rem', borderLeft: '4px solid var(--gold)', paddingLeft: '1.25rem' }}>
                  {service.summary}
                </p>
                {service.description.split('\n\n').map((para, i) => (
                  <p key={i} style={{ color: 'var(--gray-600)', lineHeight: 1.85, marginBottom: '1.25rem', fontSize: '0.97rem' }}>{para}</p>
                ))}
              </motion.div>

              {/* Benefits */}
              <div style={{ marginTop: '3rem', marginBottom: '3rem' }}>
                <span className="label" style={{ marginBottom: '0.75rem', display: 'inline-flex' }}>Key Benefits</span>
                <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.6rem', color: 'var(--navy)', marginBottom: '1.75rem' }}>What You'll Gain</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  {service.benefits.map((b, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                      style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '1rem', background: 'var(--off-white)', borderRadius: '10px', border: '1px solid var(--gray-100)' }}>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                        <FiCheck style={{ fontSize: '0.7rem', color: 'var(--navy)', strokeWidth: 3 }} />
                      </div>
                      <span style={{ fontSize: '0.85rem', color: 'var(--gray-700)', lineHeight: 1.5 }}>{b}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Process */}
              <div style={{ marginBottom: '3rem' }}>
                <span className="label" style={{ marginBottom: '0.75rem', display: 'inline-flex' }}>How It Works</span>
                <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.6rem', color: 'var(--navy)', marginBottom: '2rem' }}>Our Process</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                  {service.process.map((step, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                      style={{ display: 'flex', gap: '1.25rem', paddingBottom: i < service.process.length - 1 ? '1.75rem' : 0, position: 'relative' }}>
                      {/* Line */}
                      {i < service.process.length - 1 && (
                        <div style={{ position: 'absolute', left: 19, top: 44, bottom: 0, width: 2, background: 'linear-gradient(to bottom, #C9A84C, var(--gray-200))' }} />
                      )}
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#C9A84C', fontWeight: 700, fontSize: '0.82rem', fontFamily: 'Outfit, sans-serif', zIndex: 1 }}>
                        {step.step}
                      </div>
                      <div style={{ paddingBottom: '0.5rem' }}>
                        <h4 style={{ fontFamily: 'Fraunces, serif', fontSize: '1rem', color: 'var(--navy)', marginBottom: '0.4rem' }}>{step.title}</h4>
                        <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', lineHeight: 1.65 }}>{step.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* FAQs */}
              <div>
                <span className="label" style={{ marginBottom: '0.75rem', display: 'inline-flex' }}>FAQs</span>
                <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.6rem', color: 'var(--navy)', marginBottom: '1.75rem' }}>Frequently Asked Questions</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {service.faqs.map((faq, i) => (
                    <div key={i} style={{ background: 'white', border: '1.5px solid', borderColor: openFaq === i ? '#C9A84C' : 'var(--gray-200)', borderRadius: '12px', overflow: 'hidden', transition: 'border-color 0.2s' }}>
                      <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.1rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '1rem' }}>
                        <span style={{ fontWeight: 600, color: 'var(--navy)', fontSize: '0.92rem', lineHeight: 1.4 }}>{faq.q}</span>
                        {openFaq === i ? <FiChevronUp style={{ color: 'var(--gold)', flexShrink: 0 }} /> : <FiChevronDown style={{ color: 'var(--gray-400)', flexShrink: 0 }} />}
                      </button>
                      {openFaq === i && (
                        <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} style={{ overflow: 'hidden' }}>
                          <p style={{ padding: '0 1.25rem 1.1rem', color: 'var(--gray-600)', fontSize: '0.875rem', lineHeight: 1.75 }}>{faq.a}</p>
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div style={{ position: 'sticky', top: '6rem' }}>
              {/* Book CTA */}
              <div style={{ background: 'var(--navy)', borderRadius: '20px', padding: '2rem', marginBottom: '1.5rem', color: 'white' }}>
                <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.25rem', color: 'rgba(255,255,255,0.65)', marginBottom: '0.75rem' }}>Ready to Get Started?</h3>
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                  Book a free consultation and let our experts assess your specific needs.
                </p>
                <Link to={`/book-service?service=${encodeURIComponent(service.title)}`} className="btn btn-gold" style={{ width: '100%', justifyContent: 'center', fontSize: '0.9rem' }}>
                  Book a Consultation <FiArrowRight />
                </Link>
                <Link to="/contact" style={{ display: 'block', textAlign: 'center', marginTop: '0.75rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.target.style.color = '#C9A84C'} onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.5)'}>
                  Or contact us first →
                </Link>
              </div>

              {/* Other Services */}
              <div style={{ background: 'var(--off-white)', borderRadius: '16px', padding: '1.5rem', border: '1px solid var(--gray-100)' }}>
                <h4 style={{ fontFamily: 'Fraunces, serif', fontSize: '1rem', color: 'var(--navy)', marginBottom: '1.25rem' }}>Other Services</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {others.map(sv => (
                    <Link key={sv.id} to={`/services/${sv.id}`}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: 'white', borderRadius: '10px', border: '1px solid var(--gray-200)', color: 'var(--navy)', fontSize: '0.85rem', fontWeight: 500, transition: 'all 0.2s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#C9A84C'; e.currentTarget.style.background = '#FFFBF0'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-200)'; e.currentTarget.style.background = 'white'; }}>
                      <span style={{ fontSize: '1.2rem' }}>{sv.icon}</span>
                      <span style={{ flex: 1, lineHeight: 1.3 }}>{sv.title}</span>
                      <FiArrowRight style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }} />
                    </Link>
                  ))}
                  <Link to="/services" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.6rem', color: 'var(--gold)', fontSize: '0.82rem', fontWeight: 600, marginTop: '0.25rem' }}>
                    View All Services <FiArrowRight style={{ fontSize: '0.78rem' }} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
